import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    EmptyState,
    ErrorState,
    PaginationControls,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { PAGINATION } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import { useUsersByIdsBatch } from "@/hooks/queries/user.queries";
import { useGetDarwinConversations } from "@/queries/darwin.queries";
import { createPaginationProps } from "@/types/pagination.types";
import { formatDateRelative } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const SKELETON_ROWS = 8;
const COLUMN_COUNT = 4;

const ConversationsSearchSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    userId: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_DARWIN_CONVERSATIONS)({
    component: DarwinConversationsPage,
    validateSearch: searchValidator(ConversationsSearchSchema),
    staticData: {
        config: {
            title: "conversations",
        },
        permissions: [PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function ConversationsTableSkeleton() {
    return Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <TableRow key={index}>
            {Array.from({ length: COLUMN_COUNT }, (_, cellIndex) => (
                <TableCell key={cellIndex}>
                    <Skeleton className="h-4 w-full" />
                </TableCell>
            ))}
        </TableRow>
    ));
}

function DarwinConversationsPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const { page: pageSearch, userId } = Route.useSearch();
    const page = pageSearch ?? PAGINATION.DEFAULT_PAGE;

    const { data, isLoading, isFetching, isError } = useGetDarwinConversations({
        page,
        limit: PAGINATION.PAGE_SIZE,
        userId,
    });
    const conversations = data?.data ?? [];
    const total = data?.pagination.total ?? 0;
    const userIds = useMemo(
        () => [...new Set((data?.data ?? []).map((conversation) => conversation.userId))],
        [data?.data]
    );
    const { data: users } = useUsersByIdsBatch(userIds, userIds.length > 0);
    const usersById = useMemo(() => new Map((users ?? []).map((user) => [user.id, user])), [users]);

    const setPage = (nextPage: number) => {
        navigate({ search: (prev) => ({ ...prev, page: nextPage }), replace: true });
    };

    const setUserId = (nextUserId?: string) => {
        navigate({ search: (prev) => ({ ...prev, userId: nextUserId, page: undefined }), replace: true });
    };

    const paginationProps = createPaginationProps({ response: data, onPageChange: setPage, isLoading: isFetching });

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="flex items-center gap-2">
                        {t("conversations")}
                        {data ? (
                            <span className="text-sm font-normal text-muted-foreground">
                                {t("developer_darwin_conversations_total", { count: total })}
                            </span>
                        ) : null}
                    </span>
                    <div className="w-full sm:w-80">
                        <TeamMemberSelector
                            selectedMemberId={userId ?? null}
                            onMemberChange={(memberId) => setUserId(memberId ?? undefined)}
                            labels={{ resetSelection: "all_users" }}
                        />
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {isError ? <ErrorState title={t("developer_darwin_conversations_loading_error")} /> : null}
                {!isError && !isLoading && conversations.length === 0 ? (
                    <EmptyState variant="simple" title="developer_darwin_conversations_no_data" />
                ) : null}
                {!isError && (isLoading || conversations.length > 0) ? (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t("user")}</TableHead>
                                    <TableHead>{t("developer_darwin_last_page")}</TableHead>
                                    <TableHead>{t("updated_at")}</TableHead>
                                    <TableHead>{t("created_at")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? <ConversationsTableSkeleton /> : null}
                                {conversations.map((conversation) => (
                                    <TableRow
                                        key={conversation.id}
                                        className="cursor-pointer"
                                        onClick={() =>
                                            navigate({
                                                to: APP_ROUTES_TO.DEVELOPER_DARWIN_CONVERSATION,
                                                params: { conversationId: conversation.id },
                                            })
                                        }
                                    >
                                        <TableCell className="font-medium whitespace-nowrap">
                                            {usersById.get(conversation.userId)?.name ?? conversation.userId}
                                        </TableCell>
                                        <TableCell className="max-w-xs truncate font-mono text-xs">
                                            {conversation.lastPagePath}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {formatDateRelative(conversation.updatedAt)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-muted-foreground">
                                            {formatDateRelative(conversation.createdAt)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : null}

                {paginationProps.totalPages > 1 ? (
                    <PaginationControls
                        currentPage={page}
                        totalPages={paginationProps.totalPages}
                        onPageChange={setPage}
                        isLoading={paginationProps.isLoading}
                        size="sm"
                    />
                ) : null}
            </CardContent>
        </Card>
    );
}
